<template>
  <Dropdown
    slot="extra"
    :transfer="true"
    placement="bottom-end"
    data-html2canvas-ignore
    @on-click="exportData"
  >
    <Button>
      <i class="fas fa-list-ul" />
      Export
      <Icon
        type="ios-arrow-down"
        style="margin-right: 0"
      />
    </Button>
    <DropdownMenu slot="list">
      <DropdownItem name="csv">
        <i class="fas fa-download" /> Download as CSV files
      </DropdownItem>
      <DropdownItem name="pdf">
        <i class="fas fa-download" /> Download as PDF file
      </DropdownItem>
      <DropdownItem name="png">
        <i class="fas fa-download" /> Download as PNG image
      </DropdownItem>
      <DropdownItem
        name="googleSheet"
        divided
      >
        <i class="fab fa-google-drive" /> Export to Google Sheets
      </DropdownItem>
    </DropdownMenu>
  </Dropdown>
</template>

<script>
import html2canvas from 'html2canvas'
import JSPDF from 'jspdf'

export default {
  props: {
    name: String,
    tables: Array,
    captureElement: HTMLDivElement
  },

  methods: {
    /**
       * Exports the table in different ways
       * @param {String} format Determines how to export the data.
       */
    async exportData (format) {
      this.loading = true
      const timeOfDownload = this.$moment().format('YYYY-MM-DD_HH-mm-ss')
      const filename = `${this.name}_${timeOfDownload}`

      if (format === 'png' || format === 'pdf') {
        this.$log('dashboard_download', this.name, { title: filename, format, type: 'custom' })
        const canvas = await html2canvas(this.captureElement, {})
        const url = canvas.toDataURL('image/png', 0.5)
        if (format === 'png') {
          const fileLink = document.createElement('a')
          fileLink.href = url
          fileLink.setAttribute('download', `${filename}.png`)
          document.body.appendChild(fileLink)
          fileLink.click()
          document.body.removeChild(fileLink)
        } else {
          const doc = new JSPDF('p', 'mm')
          const pageWidth = doc.internal.pageSize.getWidth()
          const pageHeight = doc.internal.pageSize.getHeight()
          const pageRatio = pageWidth / pageHeight

          const { imgWidth, imgHeight } = await this.getImageDimensions(url)
          const imgRatio = imgWidth / imgHeight
          if (imgRatio >= 1) {
            const wc = imgWidth / pageWidth
            if (imgRatio >= pageRatio) {
              doc.addImage(url, 'PNG', 0, (pageHeight - imgHeight / wc) / 2, pageWidth, imgHeight / wc, null, 'NONE')
            } else {
              const pi = pageRatio / imgRatio
              doc.addImage(url, 'PNG', (pageWidth - pageWidth / pi) / 2, 0, pageWidth / pi, (imgHeight / pi) / wc, null, 'NONE')
            }
          } else {
            const wc = imgWidth / pageHeight
            if (1 / imgRatio > pageRatio) {
              const ip = (1 / imgRatio) / pageRatio
              const margin = (pageHeight - ((imgHeight / ip) / wc)) / 4
              doc.addImage(url, 'PNG', (pageWidth - (imgHeight / ip) / wc) / 2, -(((imgHeight / ip) / wc) + margin), pageHeight / ip, (imgHeight / ip) / wc, null, 'NONE', -90)
            } else {
              doc.addImage(url, 'PNG', (pageWidth - imgHeight / wc) / 2, -(imgHeight / wc), pageHeight, imgHeight / wc, null, 'NONE', -90)
            }
          }

          doc.save(`${filename}.pdf`)
        }

        this.loading = false
      } else if (format === 'csv') {
        this.$log('dashboard_download', this.name, { title: filename, format, type: 'custom' })
        this.exportToCSV(this.tables)
      } else if (format === 'googleSheet') {
        this.$log('dashboard_sheet_export', this.name, { title: filename })
        this.saveSheet(this.tables, filename)
      }
    },

    /**
       * Get image width and height for data url
       * @param {String} file data url
       * @return {Object} promise to solve width and height
       */
    getImageDimensions (file) {
      return new Promise(resolve => {
        var i = new Image()
        i.onload = function () {
          resolve({ imgWidth: i.width, imgHeight: i.height })
        }
        i.src = file
      })
    },

    /**
       * Convert table data to CSV format and download data
       * @param {Array} tables table data to be converted with table name
       */
    exportToCSV (tables) {
      tables.forEach(table => {
        if (!table.data.length) {
          return
        }

        const header = Object.keys(table.data[0]).join(',')
        const body = table.data.map(row => Object.values(row).join(',')).join('\n')
        const file = new Blob([[header, body].join('\n')], { type: 'text/csv' })
        const fileLink = document.createElement('a')
        fileLink.href = window.URL.createObjectURL(file)
        fileLink.setAttribute('download', `${table.name}.csv`)
        document.body.appendChild(fileLink)
        fileLink.click()
        document.body.removeChild(fileLink)
      })

      this.loading = false
    },

    /**
       * Initializes logged in gapi client, then convert and save sheet to Google Sheet
       * @param {Array} data Raw table data to be exported to Google Sheet
       * @param {String} filename Used for naming
       */
    async saveSheet (data, filename) {
      try {
        const gapi = await this.$getGapiClient()
        if (!this.$isAuthenticated()) {
          this.$refreshToken()
        }

        if (!await gapi.auth2.getAuthInstance().currentUser.get().isSignedIn()) {
          await this.$login()
        }

        const sheet = this.convertDataToSheet(data, filename)
        const request = gapi.client.sheets.spreadsheets.create(sheet)
        request.then(
          (response) => this.onSheetCreationSuccess(response.result.properties.title, response.result.spreadsheetUrl),
          (error) => this.onSheetCreationError(error.result.error.message)
        )
      } catch (error) {
        let errorMessage
        if (error.error) {
          errorMessage = error.error
        } else if (error.result && error.result.error) {
          errorMessage = error.result.error.message
        } else {
          errorMessage = error
        }
        return this.onSheetCreationError(errorMessage)
      }
    },

    /**
       * This converts table data to Google Sheet API complied format
       * @param {Array} data Represents the table data from query.
       * @param {String} filename used for naming
       * @return {Array} Converted array in Google Sheet compatible format
       */
    convertDataToSheet (data, filename) {
      const sheets = data.map(table => {
        const rowValue = table.data.map(row => {
          const values = Object.values(row).map(each => {
            if (typeof each === 'number') {
              return {
                userEnteredValue: {
                  numberValue: each
                }
              }
            } else if (typeof each === 'boolean') {
              return {
                userEnteredValue: {
                  boolValue: each
                }
              }
            } else {
              return {
                userEnteredValue: {
                  stringValue: each
                }
              }
            }
          })
          return {
            values: values
          }
        })

        const rowHead = Object.keys(table.data[0]).map(each => {
          return {
            userEnteredValue: {
              stringValue: each
            }
          }
        })

        const rows = [{ values: rowHead }, ...rowValue]
        return {
          data: [{
            rowData: rows
          }],
          properties: {
            title: table.name
          }
        }
      })

      const spreadsheet = {
        properties: {
          title: filename
        },
        sheets
      }

      return spreadsheet
    },

    /**
       * Callback function to invoke dashboard open function
       * @param {String} title Represents Google Spreadsheet title
       * @param {String} url Represents Google Spreadsheet URL
       */
    onSheetCreationSuccess (title, url) {
      this.loading = false
      window.open(url, '_blank')
    },

    /**
       * Displays a pop up to show sheet export error
       * @param {String} error represents error message to display.
       */
    onSheetCreationError (error) {
      this.loading = false
      this.$Message.error({
        content: `Error while loading to Google Sheet because ${error}`,
        duration: 8
      })
    }
  }
}
</script>
